<template>
  <div class="contact-page">
    <div class="contact-wrap">

      <!-- Paginakop -->
      <div class="contact-header">
        <nav class="breadcrumb" aria-label="Kruimelpad">
          <router-link to="/" class="bc-link"><i class="bi bi-house" aria-hidden="true"></i></router-link>
          <i class="bi bi-chevron-right bc-sep" aria-hidden="true"></i>
          <span class="bc-current">Contact</span>
        </nav>
        <h1 class="contact-title">Contact</h1>
        <p class="contact-subtitle">Stel je vraag via de chat, of stuur een bericht. We reageren binnen 1 werkdag.</p>
      </div>

      <div class="contact-grid">

        <!-- Links: gegevens + snelle kanalen -->
        <div class="contact-info">

          <!-- Voorkeurs-kanaal: live chat -->
          <a href="/klantenservice#livechat" class="chat-cta" aria-label="Start live chat met een specialist">
            <span class="chat-cta-icon"><i class="bi bi-chat-dots-fill" aria-hidden="true"></i></span>
            <div class="chat-cta-text">
              <strong>Chat met een specialist</strong>
              <span>Snelste antwoord, gratis en vrijblijvend</span>
            </div>
            <i class="bi bi-arrow-right chat-cta-arrow" aria-hidden="true"></i>
          </a>

          <!-- Overige kanalen -->
          <div class="info-items">
            <div class="info-item">
              <span class="info-icon"><i class="bi bi-envelope" aria-hidden="true"></i></span>
              <div>
                <p class="info-label">E-mail</p>
                <a href="mailto:info@plug2charge.nl" class="info-value">info@plug2charge.nl</a>
              </div>
            </div>

            <div class="info-item">
              <span class="info-icon"><i class="bi bi-geo-alt" aria-hidden="true"></i></span>
              <div>
                <p class="info-label">Postadres</p>
                <p class="info-value">Plug2Charge.nl</p>
                <p class="info-value-muted">Poortdijkstraat 13<br>4318AL Brouwershaven</p>
              </div>
            </div>

            <div class="info-item">
              <span class="info-icon"><i class="bi bi-building" aria-hidden="true"></i></span>
              <div>
                <p class="info-label">Bedrijfsgegevens</p>
                <p class="info-value-muted">KVK 76253937</p>
                <p class="info-value-muted">BTW NL003065325B15</p>
              </div>
            </div>
          </div>

          <!-- Openingstijden -->
          <div class="hours-block">
            <p class="hours-title"><i class="bi bi-clock" aria-hidden="true"></i> Reactietijd</p>
            <p class="hours-body">Maandag t/m vrijdag reageren we binnen 4 uur. In het weekend binnen 1 werkdag.</p>
          </div>
        </div>

        <!-- Rechts: contactformulier -->
        <div class="contact-form-card">
          <h2 class="form-title">Stuur een bericht</h2>

          <!-- Succes -->
          <div v-if="sent" class="form-success" role="status">
            <span class="success-icon"><i class="bi bi-check-lg" aria-hidden="true"></i></span>
            <p class="success-title">Bericht verzonden</p>
            <p class="success-sub">We nemen zo snel mogelijk contact met je op.</p>
          </div>

          <!-- Formulier -->
          <form v-else @submit.prevent="submitForm" novalidate>
            <!-- Honeypot -->
            <input v-model="form.website" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="sr-only" />

            <div class="field-row">
              <div class="field">
                <label for="contact-name" class="field-label">Naam</label>
                <input
                  id="contact-name"
                  v-model="form.name"
                  type="text"
                  required
                  placeholder="Jan de Vries"
                  class="field-input"
                  autocomplete="name"
                />
              </div>
              <div class="field">
                <label for="contact-email" class="field-label">E-mailadres</label>
                <input
                  id="contact-email"
                  v-model="form.email"
                  type="email"
                  required
                  placeholder="jan@example.nl"
                  class="field-input"
                  autocomplete="email"
                />
              </div>
            </div>

            <div class="field">
              <label for="contact-subject" class="field-label">Onderwerp</label>
              <select id="contact-subject" v-model="form.subject" class="field-input field-select">
                <option value="">Selecteer een onderwerp</option>
                <option>Vraag over een product</option>
                <option>Bestelling / levering</option>
                <option>Installatieadvies</option>
                <option>Garantie / klacht</option>
                <option>Overig</option>
              </select>
            </div>

            <div class="field">
              <label for="contact-message" class="field-label">Bericht</label>
              <textarea
                id="contact-message"
                v-model="form.message"
                required
                rows="5"
                placeholder="Typ hier je vraag of opmerking..."
                class="field-input field-textarea"
              ></textarea>
            </div>

            <p v-if="error" class="form-error" role="alert">
              <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
              {{ error }}
            </p>

            <button type="submit" :disabled="sending" class="form-submit">
              <i v-if="sending" class="bi bi-arrow-repeat spin" aria-hidden="true"></i>
              <i v-else class="bi bi-send" aria-hidden="true"></i>
              {{ sending ? 'Versturen...' : (error ? 'Opnieuw proberen' : 'Verstuur bericht') }}
            </button>
          </form>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const form    = ref({ name: '', email: '', subject: '', message: '', website: '' })
const sending = ref(false)
const sent    = ref(false)
const error   = ref('')

const submitForm = async () => {
  sending.value = true
  error.value   = ''
  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form.value)
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Versturen mislukt. Probeer het opnieuw.')
    }
    sent.value = true
  } catch (e) {
    error.value = e.message || 'Er ging iets mis. Controleer je verbinding en probeer het opnieuw.'
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
/* ── Pagina ─────────────────────────────────────────────────────────────── */
.contact-page {
  min-height: 100vh;
  background: #f6f7f9;
  padding: 2.5rem 0 4rem;
}

.contact-wrap {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1.25rem;
}

/* ── Kop ────────────────────────────────────────────────────────────────── */
.contact-header {
  margin-bottom: 2rem;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #999;
  margin-bottom: 1rem;
}

.bc-link {
  color: #999;
  text-decoration: none;
  transition: color 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.bc-link:hover { color: #111; }

.bc-sep { font-size: 0.65rem; }
.bc-current { color: #555; font-weight: 500; }

.contact-title {
  font-size: 2rem;
  font-weight: 800;
  color: #111;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 0 0 0.5rem;
}

.contact-subtitle {
  font-size: 0.9375rem;
  color: #666;
  margin: 0;
  max-width: 55ch;
}

/* ── Grid ───────────────────────────────────────────────────────────────── */
.contact-grid {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 1rem;
  align-items: start;
}

/* ── Info-kolom ─────────────────────────────────────────────────────────── */
.contact-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Live chat CTA-kaart */
.chat-cta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #FFD500;
  border-radius: 10px;
  padding: 1rem 1.1rem;
  text-decoration: none;
  transition: transform 150ms cubic-bezier(0.23, 1, 0.32, 1),
              background 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.chat-cta:hover  { background: #ffc800; transform: translateY(-1px); }
.chat-cta:active { transform: scale(0.98); }

.chat-cta-icon {
  width: 36px;
  height: 36px;
  background: rgba(0,0,0,0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #111;
  flex-shrink: 0;
}

.chat-cta-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.chat-cta-text strong {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111;
  line-height: 1.2;
}

.chat-cta-text span {
  font-size: 0.72rem;
  color: #6b5000;
}

.chat-cta-arrow {
  color: #111;
  font-size: 0.85rem;
  flex-shrink: 0;
}

/* Overige kanalen */
.info-items {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 10px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.info-icon {
  width: 32px;
  height: 32px;
  background: #fffbe6;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: #b8860b;
  flex-shrink: 0;
}

.info-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #aaa;
  margin: 0 0 0.2rem;
}

.info-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111;
  text-decoration: none;
  display: block;
  margin: 0;
  transition: color 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
a.info-value:hover { color: #b8860b; }

.info-value-muted {
  font-size: 0.825rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

/* Reactietijd */
.hours-block {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 10px;
  padding: 1rem 1.25rem;
}

.hours-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #111;
  margin: 0 0 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.hours-title i { color: #FFD500; }

.hours-body {
  font-size: 0.78rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
  max-width: 38ch;
}

/* ── Formulier-kaart ────────────────────────────────────────────────────── */
.contact-form-card {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 12px;
  padding: 2rem;
}

.form-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #111;
  margin: 0 0 1.5rem;
}

/* Velden */
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
}

.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #555;
}

.field-input {
  width: 100%;
  padding: 0.6rem 0.875rem;
  border: 1.5px solid #e9ecef;
  border-radius: 7px;
  font-size: 0.875rem;
  color: #111;
  background: #fff;
  outline: none;
  font-family: inherit;
  transition: border-color 150ms cubic-bezier(0.23, 1, 0.32, 1);
  box-sizing: border-box;
}

.field-input:focus {
  border-color: #FFD500;
  box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.15);
}

.field-textarea {
  resize: none;
  line-height: 1.5;
}

.field-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.875rem center;
  padding-right: 2.25rem;
  cursor: pointer;
}

/* Succes */
.form-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  gap: 0.5rem;
}

.success-icon {
  width: 52px;
  height: 52px;
  background: #f0fdf4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #16a34a;
  margin-bottom: 0.5rem;
}

.success-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111;
  margin: 0;
}

.success-sub {
  font-size: 0.875rem;
  color: #666;
  margin: 0;
}

/* Foutmelding */
.form-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.83rem;
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 7px;
  padding: 0.65rem 0.875rem;
  margin-bottom: 0.75rem;
}

/* Verstuur-knop */
.form-submit {
  width: 100%;
  background: #FFD500;
  color: #111;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 0.75rem;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform  150ms cubic-bezier(0.23, 1, 0.32, 1);
  font-family: inherit;
}
.form-submit:hover:not(:disabled)  { background: #ffc800; transform: translateY(-1px); }
.form-submit:active:not(:disabled) { transform: scale(0.98); }
.form-submit:disabled              { opacity: 0.6; cursor: not-allowed; }

/* Spinner */
.spin { animation: spinAnim 0.8s linear infinite; display: inline-block; }
@keyframes spinAnim { to { transform: rotate(360deg); } }

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 720px) {
  .contact-grid {
    grid-template-columns: 1fr;
  }

  .field-row {
    grid-template-columns: 1fr;
  }
}
</style>
